import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/** @type {Promise<void> | null} */
let scriptPromise = null

function loadTurnstile() {
    if (window.turnstile) return Promise.resolve()
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = TURNSTILE_SCRIPT_SRC
            script.async = true
            script.onload = () => resolve()
            script.onerror = () => {
                scriptPromise = null
                script.remove()
                reject(new Error('Turnstile failed to load'))
            }
            document.head.appendChild(script)
        })
    }
    return scriptPromise
}

/**
 * @typedef {{
 *   getToken: (timeoutMs: number) => Promise<string | null>,
 *   reset: () => void,
 * }} TurnstileHandle
 */

/**
 * Cloudflare Turnstile. It stays invisible unless Cloudflare wants the visitor
 * to tick a box. The form reads the token through the ref: getToken waits for a
 * token that is still being issued, and reset asks for a fresh one after the
 * server has spent the last. onLoadError fires when Cloudflare's script cannot
 * load (offline, or blocked by a content blocker).
 */
const TurnstileWidget = forwardRef(
    /**
     * @param {{ siteKey: string, action: string, onLoadError: () => void }} props
     * @param {React.ForwardedRef<TurnstileHandle>} ref
     */
    function TurnstileWidget({ siteKey, action, onLoadError }, ref) {
        const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null))
        const widgetIdRef = useRef(/** @type {string | null} */ (null))
        const tokenRef = useRef(/** @type {string | null} */ (null))
        const waitersRef = useRef(/** @type {Array<(token: string) => void>} */ ([]))
        const onLoadErrorRef = useRef(onLoadError)
        onLoadErrorRef.current = onLoadError

        useImperativeHandle(
            ref,
            () => ({
                getToken(timeoutMs) {
                    if (tokenRef.current) return Promise.resolve(tokenRef.current)

                    return new Promise(resolve => {
                        /** @param {string} token */
                        const waiter = token => {
                            window.clearTimeout(timer)
                            resolve(token)
                        }
                        const timer = window.setTimeout(() => {
                            waitersRef.current = waitersRef.current.filter(w => w !== waiter)
                            resolve(null)
                        }, timeoutMs)
                        waitersRef.current.push(waiter)
                    })
                },
                reset() {
                    tokenRef.current = null
                    if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current)
                },
            }),
            [],
        )

        useEffect(() => {
            let cancelled = false
            /** @param {string | null} token */
            const setToken = token => {
                tokenRef.current = token
                if (!token) return

                const waiters = waitersRef.current
                waitersRef.current = []
                waiters.forEach(waiter => waiter(token))
            }

            loadTurnstile()
                .then(() => {
                    if (cancelled || !containerRef.current || !window.turnstile) return

                    widgetIdRef.current = window.turnstile.render(containerRef.current, {
                        sitekey: siteKey,
                        action,
                        appearance: 'interaction-only',
                        // The auth page and modal are dark whatever the OS theme.
                        theme: 'dark',
                        size: 'flexible',
                        callback: token => setToken(token),
                        'expired-callback': () => setToken(null),
                        'error-callback': () => setToken(null),
                    })
                })
                .catch(error => {
                    console.warn(error)
                    if (!cancelled) onLoadErrorRef.current()
                })

            return () => {
                cancelled = true
                if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current)
                widgetIdRef.current = null
                tokenRef.current = null
            }
        }, [siteKey, action])

        return <div ref={containerRef} data-testid="signup-turnstile" />
    },
)

export default TurnstileWidget
